'use client';

import { useEffect, useMemo, useState } from 'react';
import { type CampaignCallInfo } from '@callixbrasil/client-sdk';
import { useCallOperatorCurrentCallInfo } from '@callixbrasil/client-sdk-react';
import {
  FiAlertTriangle,
  FiBriefcase,
  FiHash,
  FiPhoneCall,
  FiSmartphone,
  FiUser,
} from 'react-icons/fi';

type CodeToNameMapping = Record<string, string>;
type ContactValues = Record<string, string>;
type FinalContactData = Record<string, string>;

interface CampaignContactData {
  finalData: FinalContactData;
  isLoading: boolean;
  error: string | null;
}

// URL base para as APIs - detecta automaticamente
const API_BASE_URL =
  typeof window !== 'undefined'
    ? (((window as unknown) as Record<string, unknown>).__CALLIX_API_BASE_URL__ as string | undefined) ||
      (window.location.origin.includes('localhost') ? window.location.origin : 'https://callix-zeta.vercel.app')
    : '';

/**
 * Get session token from localStorage
 * Pattern: callix_dialer_{username}_session_token
 */
function getSessionToken(): string | null {
  if (typeof window === 'undefined') return null;

  const lastUsername = localStorage.getItem('callix_dialer_last_username');

  if (lastUsername) {
    const sessionKey = `callix_dialer_${lastUsername}_session_token`;
    const token = localStorage.getItem(sessionKey);
    if (token) {
      return token;
    }
  }

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.match(/^callix_dialer_.*_session_token$/)) {
      const token = localStorage.getItem(key);
      if (token) {
        return token;
      }
    }
  }

  return null;
}

/**
 * Get headers with authentication token
 */
function getAuthHeaders(): HeadersInit {
  const token = getSessionToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['x-session-token'] = token;
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  } else {
    try {
      const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).filter(Boolean);
      console.warn('[CallInfoDisplay] Nenhum token encontrado no localStorage. Chaves:', keys);
    } catch {
      // ignore
    }
  }

  return headers;
}

/**
 * Fetches campaign configuration to get campaignModelId
 */
async function getCampaignConfig(contactId: number): Promise<{ campaignModelId: number }> {
  const url = `${API_BASE_URL}/api/campaign-config/${contactId}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
    throw new Error(error.error || `Falha ao buscar config: ${response.status}`);
  }

  const json = await response.json();
  return json as { campaignModelId: number };
}

/**
 * Fetches code-to-name mapping from form fields
 * Returns: { "fieldCode": "Field Name" }
 */
async function getCodeToNameMapping(campaignModelId: number): Promise<CodeToNameMapping> {
  const url = `${API_BASE_URL}/api/campaign-fields/${campaignModelId}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
    throw new Error(error.error || `Falha ao buscar mapeamento: ${response.status}`);
  }

  const json = await response.json();
  return json as CodeToNameMapping;
}

// Customer-mode: fields mapping comes from form-rows/customer/{formId}
// We call our campaign-contact endpoint with source=customer&formId=...
async function getCustomerCodeToNameMapping(formId: number): Promise<CodeToNameMapping> {
  const url = `${API_BASE_URL}/api/campaign-contact/0?source=customer&formId=${encodeURIComponent(String(formId))}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
    throw new Error(error.error || `Falha ao buscar campos (cliente): ${response.status}`);
  }

  const json = await response.json();
  const mapping: CodeToNameMapping = {};
  if (json && Array.isArray(json.rows)) {
    for (const row of json.rows as Array<Array<{ code: string; name: string }>>) {
      for (const field of row) {
        if (field && field.code && field.name) {
          mapping[field.code] = field.name;
        }
      }
    }
  }
  return mapping;
}

/**
 * Fetches contact values (actual data with field codes as keys)
 * Returns: { "fieldCode": "value" }
 */
async function getContactValues(contactId: number): Promise<ContactValues> {
  const url = `${API_BASE_URL}/api/campaign-contact/${contactId}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
    throw new Error(error.error || `Falha ao buscar valores: ${response.status}`);
  }

  const json = await response.json();
  return json as ContactValues;
}

// Customer-mode: values come from form-data/customer/{customerId}
// We call our campaign-fields endpoint with source=customer&customerId=...
async function getCustomerContactValues(customerId: number): Promise<ContactValues> {
  const url = `${API_BASE_URL}/api/campaign-fields/0?source=customer&customerId=${encodeURIComponent(String(customerId))}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
    throw new Error(error.error || `Falha ao buscar valores (cliente): ${response.status}`);
  }

  const json = await response.json();
  return json as ContactValues;
}

function findValue(finalData: FinalContactData, candidates: string[]): string | undefined {
  const entries = Object.entries(finalData);
  const lowerCandidates = candidates.map((item) => item.toLowerCase());

  for (const [key, value] of entries) {
    if (lowerCandidates.includes(key.toLowerCase())) {
      return value;
    }
  }

  return undefined;
}

function formatPhoneValue(raw: string | undefined): string {
  if (!raw) return '-';

  const digits = raw.replace(/\D/g, '');

  if (digits.length >= 10) {
    const ddd = digits.slice(0, 2);
    const core = digits.slice(2, digits.length - 4);
    const tail = digits.slice(-4);
    const formattedCore = core.length > 5 ? `${core.slice(0, core.length - 4)} ${core.slice(core.length - 4)}` : core;

    return `(${ddd}) ${formattedCore}-${tail}`.replace(/\s+/g, ' ').trim();
  }

  return raw.trim();
}

export function CallInfoDisplay() {
  const callInfo = useCallOperatorCurrentCallInfo();

  useEffect(() => {
    console.log('[CallInfoDisplay] Call info atualizado:', {
      callInfo,
      type: callInfo?.type,
      hasCampaignInfo: callInfo?.type === 'campaign',
      info: callInfo?.type === 'campaign' ? callInfo.info : null,
      campaignContact: callInfo?.type === 'campaign' ? callInfo.info.campaignContact : null,
    });
  }, [callInfo]);

  if (callInfo?.type === 'campaign') {
    return <CampaignCallInfoDisplay campaignCallInfo={callInfo.info} />;
  }

  if (callInfo) {
    return (
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
        <p className="text-xs font-semibold text-amber-700">Chamada manual (sem informações de campanha)</p>
      </div>
    );
  }

  return null;
}

export function CampaignCallInfoDisplay(props: { campaignCallInfo: CampaignCallInfo }) {
  const { campaign, campaignContact } = props.campaignCallInfo;

  const [contactData, setContactData] = useState<CampaignContactData>({
    finalData: {},
    isLoading: false,
    error: null,
  });

  const [fetchedContactId, setFetchedContactId] = useState<number | null>(null);

  useEffect(() => {
    if (!campaignContact?.id) {
      return;
    }

    if (fetchedContactId === campaignContact.id) {
      return;
    }

    let cancelled = false;

    const fetchContactData = async () => {
      setContactData({ finalData: {}, isLoading: true, error: null });

      try {
        const customer = (campaignContact as unknown as { customer?: { id?: number; formId?: number } }).customer;
        let codeToNameMap: CodeToNameMapping = {};
        let contactValues: ContactValues = {};

        if (customer?.id && customer?.formId) {
          [codeToNameMap, contactValues] = await Promise.all([
            getCustomerCodeToNameMapping(customer.formId),
            getCustomerContactValues(customer.id),
          ]);
        } else {
          const config = await getCampaignConfig(campaignContact.id);
          if (!config.campaignModelId) {
            throw new Error('campaignModelId não encontrado');
          }

          [codeToNameMap, contactValues] = await Promise.all([
            getCodeToNameMapping(config.campaignModelId),
            getContactValues(campaignContact.id),
          ]);
        }

        const finalData: FinalContactData = {};
        Object.entries(contactValues).forEach(([code, value]) => {
          if (code in codeToNameMap) {
            finalData[codeToNameMap[code]] = value;
          }
        });

        if (!cancelled) {
          setContactData({
            finalData,
            isLoading: false,
            error: null,
          });
          setFetchedContactId(campaignContact.id);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CampaignCallInfoDisplay] Erro ao buscar dados:', error);
          const errorMessage = error instanceof Error ? error.message : 'Erro ao buscar dados do contato';
          setContactData({
            finalData: {},
            isLoading: false,
            error: errorMessage,
          });
        }
      }
    };

    void fetchContactData();

    return () => {
      cancelled = true;
    };
  }, [campaignContact, campaignContact?.id, fetchedContactId]);

  const prioritizedFields = useMemo(
    () => [
      { label: 'Decisor', keys: ['DECISOR'], icon: <FiUser className="text-sm" /> },
      { label: 'Empresa', keys: ['EMPRESA', 'RAZAO SOCIAL'], icon: <FiBriefcase className="text-sm" /> },
      { label: 'Dívida ativa', keys: ['DIVIDA ATIVA', 'DÍVIDA ATIVA'], icon: <FiHash className="text-sm" /> },
      { label: 'Telefone', keys: ['TELEFONE', 'CELULAR', 'WHATSAPP'], icon: <FiSmartphone className="text-sm" /> },
    ],
    []
  );

  const highlightData = useMemo(() => {
    return prioritizedFields.map((field) => {
      const rawValue =
        (field.label === 'Telefone'
          ? findValue(contactData.finalData, field.keys) ?? campaignContact?.phoneNumber
          : findValue(contactData.finalData, field.keys)) || '';

      const display =
        field.label === 'Telefone' ? formatPhoneValue(rawValue) : (rawValue || '-').toString().trim() || '-';

      return {
        ...field,
        value: display,
        isEmpty: !rawValue,
      };
    });
  }, [campaignContact?.phoneNumber, contactData.finalData, prioritizedFields]);

  const hasData = highlightData.some((item) => !item.isEmpty);

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-inner">
            <FiPhoneCall className="text-base" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Campanha</p>
            <p className="truncate text-sm font-bold text-slate-900">
              {campaign?.name || 'Chamada de campanha'}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Em chamada
        </span>
      </div>

      {contactData.isLoading && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
          <div className="mx-auto mb-2 inline-block h-7 w-7 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
          <p className="text-xs font-medium text-slate-600">Carregando dados do contato...</p>
        </div>
      )}

      {contactData.error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
          <div className="mb-1 flex items-center gap-2">
            <FiAlertTriangle className="text-sm text-rose-600" />
            <p className="text-xs font-semibold text-rose-700">Erro ao carregar dados</p>
          </div>
          <p className="ml-6 text-[11px] text-rose-600">{contactData.error}</p>
          {contactData.error.includes('Muitas requisições') && (
            <button
              onClick={() => setFetchedContactId(null)}
              className="mt-2 ml-6 text-[11px] font-semibold text-rose-700 underline hover:text-rose-800"
            >
              Tentar novamente
            </button>
          )}
        </div>
      )}

      {!contactData.isLoading && !contactData.error && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {highlightData.map((item) => (
            <div
              key={item.label}
              className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-slate-50 p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
            >
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {item.icon}
                <span className="truncate">{item.label}</span>
              </div>
              <p className="break-words text-sm font-bold leading-tight text-slate-900">
                {item.value || '-'}
              </p>
            </div>
          ))}
        </div>
      )}

      {!contactData.isLoading && !contactData.error && !hasData && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs font-medium text-slate-600">
          Nenhuma informação de campanha disponível para este contato.
        </div>
      )}
    </div>
  );
}
